/**
 * P5 (Juni 2026) – Wiederverwendbares Banner für serverseitige P2-Sperren/Limits.
 *
 * Zeigt eine freundliche, nicht-technische Erklärung an, WENN das Backend eine
 * Aktion wegen Account-Status (expired/suspended) oder Limit blockiert hat.
 * Rendert `null`, wenn der übergebene Fehler KEIN Policy-Fehler ist – kann also
 * gefahrlos überall eingehängt werden.
 *
 * Die Komponente trifft KEINE Sicherheitsentscheidung; sie erklärt nur.
 */
import { useCallback, useState } from "react";
import { AlertTriangle, ShieldAlert, X } from "lucide-react";

import { isAccountPolicyError, normalizePolicyError } from "@/lib/accountPolicy";

const VARIANT_STYLE = {
  warning: {
    wrap: "bg-amber-50 border-amber-300 text-amber-900",
    icon: "text-amber-600",
    Icon: AlertTriangle,
  },
  danger: {
    wrap: "bg-red-50 border-red-300 text-red-900",
    icon: "text-red-600",
    Icon: ShieldAlert,
  },
};

export function AccountPolicyBanner({ error, policy, onDismiss, onBuyPaket, role, className = "" }) {
  // FIX-01b-2 (E9): role="employee" → Mitarbeitertext bei read_only_* (außer suspended).
  const p = policy || normalizePolicyError(error, { role });
  if (!p) return null;
  const style = VARIANT_STYLE[p.variant] || VARIANT_STYLE.danger;
  const Icon = style.Icon;

  return (
    <div
      role="alert"
      data-testid="account-policy-banner"
      data-policy-code={p.code}
      className={`rounded-lg border p-4 flex items-start gap-3 ${style.wrap} ${className}`}
    >
      <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${style.icon}`} />
      <div className="flex-1 min-w-0">
        <p data-testid="account-policy-banner-title" className="font-semibold leading-snug">
          {p.title}
        </p>
        <p data-testid="account-policy-banner-text" className="text-sm mt-1 opacity-90">
          {p.text}
        </p>
        {p.cta && (
          <p data-testid="account-policy-banner-cta" className="text-sm font-medium mt-2">
            →{" "}
            {p.cta_href ? (
              // BUCH-01 (Betreiberfreigabe 12.10.): CTA mit Ziel (Tarifkarte).
              <a href={p.cta_href} className="underline" data-testid="account-policy-banner-cta-link">
                {p.cta}
              </a>
            ) : (
              p.cta
            )}
          </p>
        )}
        {/* WELT-02b (Betreiberfreigabe 12.10.): Kauf-Button nur für berechtigte
            Zahlkunden (paketPurchase-Flag) und nur, wenn der Flow einen Handler
            übergibt. Die Berechtigungsprüfung bleibt serverseitig. */}
        {p.paketPurchase && onBuyPaket && (
          <div className="mt-3">
            <button
              type="button"
              data-testid="account-policy-banner-buy-paket"
              onClick={onBuyPaket}
              className="inline-flex items-center rounded-md border border-amber-400 bg-amber-100 px-3 py-1.5 text-sm font-semibold text-amber-900 hover:bg-amber-200 transition-colors"
            >
              Welten-Paket kaufen
            </button>
          </div>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          data-testid="account-policy-banner-dismiss"
          onClick={onDismiss}
          aria-label="Hinweis schließen"
          className="shrink-0 rounded p-1 hover:bg-black/5 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/**
 * Kleiner Hook für die einheitliche Verdrahtung in betroffenen Flows.
 *
 * Nutzung im catch:
 *   if (!handlePolicyError(e)) toast.error(e?.response?.data?.detail || "…");
 * Im Render:
 *   <AccountPolicyBanner error={policyError} onDismiss={clearPolicyError} />
 */
export function usePolicyBanner() {
  const [policyError, setPolicyError] = useState(null);
  const handlePolicyError = useCallback((err) => {
    if (isAccountPolicyError(err)) {
      setPolicyError(err);
      return true;
    }
    return false;
  }, []);
  const clearPolicyError = useCallback(() => setPolicyError(null), []);
  return { policyError, handlePolicyError, clearPolicyError };
}
